import React, { useRef, useLayoutEffect } from "react";
import PropTypes from "prop-types";

// SVG logo:
import NpHeaderLogo from "./images/NpHeaderLogo";

// components:
import { OverflowMenu } from "components/OverflowMenu";
import { ProfileMenu } from "components/ProfileMenu";
import DefaultSearch from "./DefaultSearch";

// links:
import { homePath, photosLandingPath, ICON_LANDING_PAGE } from "data/links";

// shared styles/components:
import { Col } from "core/oldGrid";
import colors from "core/colors";
import {
    HeaderLogoLink,
    SearchWrapper,
    MobileContentWrapper,
    LogoAndSearch,
    MenuAndAvatar,
    HeaderRow
} from "./styles";

// for hero/header show/hide search:
import { useViewport } from "js/hooks/useViewport";
import {
    scrollPosShowHideSearch,
    scrollSearchBarScrollingDown,
    scrollSearchBarScrollingUp
} from "./helpers";

const MobileContent = ({
    transparent,
    currentUser,
    loginLink,
    joinLink,
    handleLogOut,
    isHeroSearchV2
}) => {
    const searchWrapperRef = useRef(null);

    const loggedOut = !currentUser || currentUser.isAnonymous;

    // for hero/header show/hide search:
    const { browserWidth } = useViewport();

    const scrollPositionShowHideHeroSearch = scrollPosShowHideSearch({
        browserWidth,
        isHeroSearchV2
    });

    const handleScrollSearchBar = () => {
        if (
            searchWrapperRef.current &&
            searchWrapperRef.current.style.display !== "block" &&
            (location.pathname === homePath.url ||
                location.pathname === photosLandingPath ||
                location.pathname === ICON_LANDING_PAGE)
            // ^^ add location.pathname === path-to-page when isHeroSearchV2
        ) {
            searchWrapperRef.current.setAttribute("style", "display:block");
        }

        const heroSearchWrapper = document.getElementById(
            "hero-search-wrapper"
        );

        if (window.pageYOffset > scrollPositionShowHideHeroSearch) {
            scrollSearchBarScrollingDown({
                heroSearchWrapper,
                isHeroSearchV2,
                searchWrapperRef
            });
        } else if (window.pageYOffset < scrollPositionShowHideHeroSearch) {
            scrollSearchBarScrollingUp({
                heroSearchWrapper,
                isHeroSearchV2,
                searchWrapperRef
            });
        }
    };

    useLayoutEffect(() => {
        if (
            searchWrapperRef.current &&
            searchWrapperRef.current.style.display !== "none" &&
            (location.pathname === homePath.url ||
                location.pathname === photosLandingPath ||
                location.pathname === ICON_LANDING_PAGE)
            // ^^ add location.pathname === path-to-page when isHeroSearchV2
        ) {
            searchWrapperRef.current.setAttribute("style", "display:none");
        }
        window.addEventListener("scroll", handleScrollSearchBar);

        return () => {
            window.removeEventListener("scroll", () => handleScrollSearchBar);
        };
    }, []);

    return (
        <MobileContentWrapper>
            <HeaderRow middle="sm">
                <Col sm={9}>
                    <LogoAndSearch alignItems="center">
                        <HeaderLogoLink
                            href={homePath.url}
                            isHeroSearchV2={isHeroSearchV2}
                        >
                            <NpHeaderLogo
                                title={homePath.alt}
                                fillColor={
                                    isHeroSearchV2
                                        ? colors.primary.snow
                                        : colors.secondary.onyx
                                }
                                width={38}
                                height={10}
                            />
                        </HeaderLogoLink>

                        <SearchWrapper ref={searchWrapperRef}>
                            <DefaultSearch />
                        </SearchWrapper>
                    </LogoAndSearch>
                </Col>
                <Col sm={3}>
                    <MenuAndAvatar
                        loggedOut={loggedOut}
                        flexDirection={loggedOut && "row-reverse"}
                        alignItems="center"
                        justifyContent={loggedOut ? "flex-start" : "flex-end"}
                    >
                        <OverflowMenu
                            transparent={transparent}
                            isHeroSearchV2={isHeroSearchV2}
                        />
                        <ProfileMenu
                            isHeroSearchV2={isHeroSearchV2}
                            currentUser={currentUser}
                            loginLink={loginLink}
                            joinLink={joinLink}
                            handleLogOut={handleLogOut}
                        />
                    </MenuAndAvatar>
                </Col>
            </HeaderRow>
        </MobileContentWrapper>
    );
};

MobileContent.propTypes = {
    isHeroSearchV2: PropTypes.bool,
    transparent: PropTypes.bool,
    currentUser: PropTypes.object,
    loginLink: PropTypes.oneOfType([PropTypes.node, PropTypes.element])
        .isRequired,
    joinLink: PropTypes.oneOfType([PropTypes.node, PropTypes.element])
        .isRequired,
    handleLogOut: PropTypes.func.isRequired
};

export { MobileContent };
